import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, clearLoginError, resetLogoutState } from '../../../store/auth/login/actions';
import { isAuthenticatedByCookie } from '../../../helpers/cookie-utils';
import {
    Col,
    Container,
    Row,
} from "reactstrap"
import atslogo from '../../../assets/images/ats-logo.png';

const LoginPage = () => {
    const [companyCode, setCompanyCode] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Get loading and error state from Redux
    const { loading, error, isAuthenticated, isLoggedOut } = useSelector(state => ({
        loading: state.Login.loading,
        error: state.Login.error,
        isAuthenticated: isAuthenticatedByCookie(),
        isLoggedOut: state.Login.isLoggedOut, // Track logout state
    }));

    useEffect(() => {
        // Clear any previous errors when component mounts
        dispatch(clearLoginError());
    }, [dispatch]);

    // Clear logout state when user starts interacting with the form
    useEffect(() => {
        if (isLoggedOut && (companyCode || email || password)) {
            // User is typing, clear the logout state
            dispatch(resetLogoutState());
        }
    }, [companyCode, email, password, isLoggedOut, dispatch]);

    // Check if user gets authenticated and navigate
    // But don't auto-navigate if user just logged out
    useEffect(() => {
        if (isAuthenticated && !loading && !error && !isLoggedOut) {
            console.log("User authenticated, navigating to dashboard");
            navigate('/dashboard');
        }
    }, [isAuthenticated, loading, navigate, error, isLoggedOut]);

    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (!companyCode || !email || !password) {
            return;
        }

        setIsSubmitting(true);
        
        try {
            console.log("Dispatching login with navigate function:", navigate);
            dispatch(loginUser(
                {
                    email: email,
                    company_code: companyCode,
                    password: password,
                },
                navigate
            ));
        } catch (err) {
            console.error('Login submission error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = companyCode && email && password;

    return (
        <div className="flex justify-content-center align-items-center min-h-screen login-page">
            <Card style={{ width: '30rem' }} className='login-card'>
                {error && (
                    <Message 
                        severity="error" 
                        text={typeof error === 'string' ? error : 'Login failed. Please check your credentials.'} 
                        className="mb-3"
                    />
                )}
                
                <div className="flex justify-content-center align-items-center ">
                    <img src={atslogo} width="150" height="64" className='mb-2' alt="Company Logo" />
                </div>
                
                <form onSubmit={handleLogin} className="p-fluid">
                    {/* Company Code Field */}
                    <Row className='mb-3'>
                        <Col lg={12}>
                            <h3 className='login-head mb-3'>Sign In</h3>
                        </Col>
                        <Col lg={12}>
                            <div className="p-field">
                                <label htmlFor="companyCode">Company Code *</label>
                                <InputText
                                    id="companyCode"
                                    value={companyCode}
                                    onChange={(e) => setCompanyCode(e.target.value)}
                                    placeholder="e.g., COMP001"
                                    required
                                    disabled={loading || isSubmitting}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Email Field */}
                    <Row className='mb-3'>
                        <Col lg={12}>
                            <div className="p-field">
                                <label htmlFor="email">Email Address *</label>
                                <InputText
                                    id="email"
                                    // type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="user@company.com"
                                    required
                                    disabled={loading || isSubmitting}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row className='mb-3'>
                        <Col lg={12}>
                            {/* Password Field */}
                            <div className="p-field">
                                <label htmlFor="password">Password *</label>
                                <Password
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    toggleMask
                                    placeholder="Enter your password"
                                    required
                                    disabled={loading || isSubmitting}
                                    feedback={false}
                                />
                            </div>
                        </Col>
                    </Row>
                    
                    {/* Forgot Password Link */}
                    <div className="p-field d-flex justify-content-end mb-2">
                        <Link to="/forgot-password" className="p-link">Forgot Password?</Link>
                    </div>

                    <Row>
                        <Col lg={12}>
                            <div className='d-flex justify-content-center'>
                                <Button
                                    type="submit"
                                    className="btn btn-primary btn-main mb-4"
                                    disabled={!isFormValid || loading || isSubmitting}
                                    loading={loading || isSubmitting}
                                >
                                    {(loading || isSubmitting) ? 'Signing In...' : 'Login'}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                    {/* <div className="p-field d-flex justify-content-center mb-2">
                      New Company?  <Link to="/register" className="p-link"> Register</Link>
                    </div> */}
                    <Col lg={12} className='text-center'>
                        <p>New Company? <span style={{ color: 'blue', cursor: 'pointer' }} onClick={() => navigate("/register", { replace: true })}>Register</span></p>
                    </Col>
                </form>
            </Card>

            <footer className="footer footer-back detailsformfooter loginfooter">
                <Container fluid={true}>
                    <Row className="d-flex">
                        <Col lg={6}>
                            <p className="d-flex justify-content-start mb-0"> &copy; 2025 Pranathi Software Services Pvt. Ltd. All rights reserved.</p>
                        </Col>
                        <Col lg={6}>
                            <div className="d-flex justify-content-end">
                                <Link to="">
                                    <p className="me-3 mb-0">Privacy Policy</p>
                                </Link>
                                <Link to="">
                                    <p className="me-3 mb-0">Terms of Service</p>
                                </Link>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </footer>
        </div>
    );
};

export default LoginPage;
